import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion } from "framer-motion";
import { Shield, Users, Eye, Settings, Clock, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const roleColors = {
  owner: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  admin: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  ciso: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  auditor: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  analyst: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  viewer: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400"
};

const RBACSettings = () => {
  const { token, user: currentUser } = useAuth();
  const { t } = useI18n();
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [rRes, uRes, tRes] = await Promise.all([
        axios.get(`${API}/rbac/roles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/rbac/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/audit-trail?limit=30`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setRoles(rRes.data);
      setUsers(uRes.data);
      setAuditTrail(tRes.data);
    } catch (error) {
      console.error("Failed:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateRole = async (userId, role) => {
    try {
      await axios.put(`${API}/rbac/users/${userId}/role?role=${role}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Role updated to ${role}`);
      fetchAll();
    } catch (error) {
      toast.error(extractError(error, "Failed to update role"));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="rbac-settings-page">
      <PageHeader
        title={t("rbac.title")}
        description={t("rbac.subtitle")}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Users & Roles */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-border" data-testid="users-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5" /> Organization Users</CardTitle>
              <CardDescription>Assign roles to control module access</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {users.map(u => (
                <div key={u.user_id} className="flex items-center justify-between gap-4 p-3 rounded-lg bg-muted/30" data-testid={`user-row-${u.user_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{u.name || u.email}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                  <Badge variant="outline" className={roleColors[u.role] || ""}>{u.role}</Badge>
                  {currentUser?.role === "owner" && u.user_id !== currentUser?.user_id && (
                    <Select value={u.role} onValueChange={(v) => updateRole(u.user_id, v)}>
                      <SelectTrigger className="w-36" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Roles Reference */}
          <Card className="border-border" data-testid="roles-card">
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Shield className="w-5 h-5" /> Role Definitions</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {roles.map(r => (
                <div key={r.id} className="p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className={roleColors[r.id] || ""}>{r.name}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{r.description}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(r.permissions[0] === "*" ? ["All Modules"] : r.permissions.slice(0, 6)).map((p, i) => (
                      <Badge key={i} variant="secondary" className="text-[10px]">{p}</Badge>
                    ))}
                    {r.permissions[0] !== "*" && r.permissions.length > 6 && (
                      <Badge variant="secondary" className="text-[10px]">+{r.permissions.length - 6} more</Badge>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Audit Trail */}
        <Card className="border-border" data-testid="audit-trail-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Clock className="w-5 h-5" /> Audit Trail</CardTitle>
            <CardDescription>Recent activity log</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[calc(100vh-22rem)]">
              <div className="space-y-3">
                {auditTrail.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No activity recorded yet</p>
                ) : (
                  auditTrail.map(t => (
                    <div key={t.trail_id} className="p-2 rounded bg-muted/20 text-xs">
                      <p className="font-medium">{t.action}</p>
                      <p className="text-muted-foreground">{t.details}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{new Date(t.timestamp).toLocaleString()}</p>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RBACSettings;
